import React, {
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useState,
} from 'react'
import classNames from 'classnames'
import moment from 'moment'

import { ClearButton } from './button'
import { DimensionSelector } from './collapsable-filters'
import { LoadingLabel } from './loader'
import SelectBox from './select-box'
import { DimensionOption, SelectedDimensions } from '../api/types'
import { AvailableDimension } from '../context/reducers/graphql-reducer'
import { granularityFilterData, performanceDates } from '../core/variables'
import useLogAction from '../hooks/useLogAction'
import styles from '../styles/usage-report-filter.module.scss'
import StyledDatePicker from './date-picker'

const yesterday = new Date(Date.now())
yesterday.setDate(yesterday.getDate() - 1)

interface UsageReportFiltersProps {
  isMobile: boolean
  currentDate: string
  setCurrentDate: Dispatch<SetStateAction<string>>
  granularity: Granularity
  setGranularity: Dispatch<SetStateAction<Granularity>>
  setStartDate: Dispatch<SetStateAction<Date>>
  setEndDate: Dispatch<SetStateAction<Date>>
  availableDimensions: AvailableDimension[]
  selectedDimensions: SelectedDimensions
  setSelectedDimensions: Dispatch<SetStateAction<SelectedDimensions>>
  loading: boolean
}

const UsageReportFilters = ({
  isMobile,
  currentDate,
  setCurrentDate,
  granularity,
  setGranularity,
  setStartDate,
  setEndDate,
  availableDimensions,
  selectedDimensions,
  setSelectedDimensions,
  loading = true,
}: UsageReportFiltersProps): React.ReactElement => {
  const logAction = useLogAction()

  const [showCalendar, setShowCalendar] = useState(currentDate === 'custom')
  const [customDates, setCustomDates] = useState<[Date | null, Date | null]>([
    null,
    null,
  ])

  const onResetFilters = () => {
    setSelectedDimensions({})
  }

  // Placeholder to show instead of 'custom date range'
  const customDateCopy = useMemo(() => {
    const [start, end] = customDates

    if (!start || !end) return 'custom date range'

    const st = moment(start).format('DD/MM/YYYY')
    const en = moment(end).format('DD/MM/YYYY')

    return `${st} - ${en}`
  }, [customDates])

  // Remove custom dates when not custom
  useEffect(() => {
    if (currentDate !== 'custom') setCustomDates([null, null])
  }, [currentDate])

  // Set custom dates for report to update
  useEffect(() => {
    const [start, end] = customDates

    if (!start || !end) return

    setStartDate(start)
    setEndDate(end)
    setCurrentDate('custom')
    setGranularity('daily')

    const st = moment(start).format('YYYY-MM-DD')
    const en = moment(end).format('YYYY-MM-DD')
    const days = moment(end).diff(start, 'days')

    if (st !== en) {
      logAction({
        variables: {
          action: 'date-range-updated',
          extra: `custom - start: ${st} end: ${en} days: ${days}`,
          websiteSection: 'report',
          functionName: 'updateDateRange',
          pagePath: '/report/usage',
        },
      })
    }
  }, [customDates])

  const onChangeFilters = (val: DimensionOption, item: AvailableDimension) => {
    const dimension = {
      ...selectedDimensions,
    }
    delete dimension[item.dimensionParameterID]
    // when an array is empty, the filter should get removed
    if (
      Array.isArray(val.dimensionOptions) &&
      val.dimensionOptions.length !== 0
    ) {
      dimension[item.dimensionParameterID] = val
    }
    setSelectedDimensions({
      ...dimension,
    })
  }

  return (
    <div className={classNames(styles.box, styles.topRowWrapper)}>
      <div className={styles.topRowBox}>
        <div className={styles.topRow}>
          <div className={styles.headerIntro}>
            <div className={styles.introCopyWrapper}>
              {loading ? (
                <div className={styles.introCopyBox}>
                  <LoadingLabel label="Loading filters" />
                </div>
              ) : (
                <>
                  <div className={styles.introCopyBox}>
                    <span className={styles.introCopy}>Show data&nbsp;</span>
                    {availableDimensions.length > 0 &&
                      availableDimensions.some(
                        (dim) =>
                          dim.dimensionOptions &&
                          dim.dimensionOptions.length > 1,
                      ) && (
                        <>
                          <span className={styles.introCopy}> for </span>
                          {availableDimensions.map((dim, index) => {
                            const hasValues = Object.prototype.hasOwnProperty.call(
                              selectedDimensions,
                              dim.dimensionParameterID,
                            )

                            if (
                              dim &&
                              dim.dimensionOptions &&
                              dim.dimensionOptions.length < 2
                            ) {
                              return <></>
                            }

                            return (
                              <React.Fragment key={JSON.stringify(dim)}>
                                <DimensionSelector
                                  className={styles.dimensionSelectors}
                                  greyChecklist
                                  defaultValue={
                                    !hasValues
                                      ? {
                                          dimensionParameterID:
                                            dim.dimensionParameterID,
                                          dimensionOptions: [],
                                        }
                                      : selectedDimensions[
                                          dim.dimensionParameterID
                                        ]
                                  }
                                  item={dim}
                                  onChange={(val: DimensionOption) => {
                                    onChangeFilters(val, dim)
                                  }}
                                  headlineFilter
                                />
                                {!isMobile &&
                                  index < availableDimensions.length - 1 && (
                                    <span className={styles.introCopy}>, </span>
                                  )}
                              </React.Fragment>
                            )
                          })}
                        </>
                      )}
                  </div>
                  <div className={styles.introCopyBox}>
                    <span className={styles.introCopy}> over </span>
                    <SelectBox
                      key={currentDate}
                      variant="grey"
                      id="dateRange"
                      className={styles.successMetricSelector}
                      isSearchable={false}
                      defaultValue={performanceDates.find(
                        (date) => date.metricValue === 'thisYear',
                      )}
                      placeholder={customDateCopy}
                      labelKey="displayName"
                      valueKey="metricValue"
                      value={
                        showCalendar
                          ? null
                          : performanceDates.find(
                              (date) => date.metricValue === currentDate,
                            )
                      }
                      options={performanceDates}
                      onChange={(newValue) => {
                        if (!newValue) return

                        const { metricValue } = newValue

                        if (metricValue === 'custom') {
                          setShowCalendar(true)
                        } else {
                          setShowCalendar(false)
                          setCurrentDate(metricValue)

                          if (metricValue !== 'thisYear') {
                            setGranularity('daily')
                          }

                          logAction({
                            variables: {
                              action: 'date-range-updated',
                              extra: metricValue,
                              websiteSection: 'report',
                              functionName: 'updateDateRange',
                              pagePath: '/report/usage',
                            },
                          })
                        }
                      }}
                    />
                    {showCalendar && (
                      <StyledDatePicker
                        buttonOnly
                        wrapperClassName={styles.dateWrapper}
                        startOpen
                        selectsRange
                        startDate={customDates[0]}
                        endDate={customDates[1]}
                        onChange={(dates) => setCustomDates(dates)}
                        maxDate={new Date(Date.now())}
                      />
                    )}
                  </div>
                  <div className={styles.introCopyBox}>
                    <span className={styles.introCopy}> grouped by </span>
                    <SelectBox
                      key={granularity}
                      variant="grey"
                      id="granularity"
                      className={styles.granularitySelector}
                      labelKey="name"
                      isSearchable={false}
                      defaultValue={granularityFilterData.find(
                        (item) => item.value === 'monthly',
                      )}
                      value={granularityFilterData.find(
                        (option) => option.value === granularity,
                      )}
                      options={granularityFilterData}
                      onChange={(newValue) => {
                        if (!newValue) return

                        const { value } = newValue

                        if (
                          value === 'quarterly' ||
                          value === 'monthly' ||
                          value === 'weekly' ||
                          value === 'daily'
                        ) {
                          setGranularity(value)

                          logAction({
                            variables: {
                              action: 'granularity-updated',
                              extra: value,
                              websiteSection: 'report',
                              functionName: 'updateGranularity',
                              pagePath: '/report/usage',
                            },
                          })
                        }
                      }}
                    />
                  </div>
                  {Object.keys(selectedDimensions).length !== 0 && (
                    <ClearButton
                      className={styles.resetButton}
                      onPress={() => onResetFilters()}
                    >
                      Reset filters
                    </ClearButton>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default UsageReportFilters
